import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TextField, Button, Avatar, Stack, Snackbar, Alert, InputAdornment, IconButton } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const ProfilePage = () => {
    const [user, setUser] = useState(null);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [bio, setBio] = useState('');
    const [avatar, setAvatar] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState(null);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState(''); // NEW STATE
    const [showPassword, setShowPassword] = useState(false); // NEW STATE for visibility toggle
    const [passwordError, setPasswordError] = useState(false); // NEW STATE for validation

    // Snackbar state
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState('success'); 
    const [snackbarMessage, setSnackbarMessage] = useState('');

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const { data } = await axios.get('https://ipms.insollex.com/api/user');
                setUser(data);
                setFirstName(data.firstName);
                setLastName(data.lastName);
                setEmail(data.emailAddress);
                setBio(data.bio || '');

                if (data.avatar) {
                    setAvatarPreview(getFullAvatarUrl(data.avatar));
                } else {
                    setAvatarPreview(null);
                }
                setAvatar(null);
            } catch (e) {
                console.error('Error fetching user data:', e);
                showSnackbar('Error fetching user data', 'error');
            }
        };

        fetchUserData();
    }, []);

    const getFullAvatarUrl = (avatarPath) => {
        if (!avatarPath) return '';
        return avatarPath.startsWith('/static/')
            ? `https://ipms.insollex.com${avatarPath}`
            : `https://ipms.insollex.com/static/${avatarPath.replace(/^\/+/, '')}`;
    };

    // Handle profile picture change
    const handleAvatarChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarPreview(reader.result);
            };
            reader.readAsDataURL(file);
            setAvatar(file);
        } else {
            setAvatarPreview(null);
            setAvatar(null);
        }
    };

    // Show snackbar helper
    const showSnackbar = (message, severity = 'success') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    // NEW: Toggle password visibility
    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };
    
    // NEW: Handle form submission for updating user data
    const handleSubmit = async (event) => {
        event.preventDefault();

        // **NEW VALIDATION LOGIC**
        if (password.trim() || confirmPassword.trim()) {
            if (password !== confirmPassword) {
                setPasswordError(true);
                showSnackbar('Passwords do not match.', 'error');
                return;
            }
        }
        setPasswordError(false); // Clear error if validation passes

        const formData = new FormData();
        formData.append('firstName', firstName);
        formData.append('lastName', lastName);
        formData.append('emailAddress', email);
        formData.append('bio', bio);

        if (password.trim()) {
            formData.append('password', password);
        }

        if (avatar) {
            formData.append('avatar', avatar);
        }

        try {
            const response = await axios.put('https://ipms.insollex.com/api/user', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            showSnackbar('Profile updated successfully', 'success');

            // Update local state with new user info
            if (response.data.user) {
                setUser(response.data.user);
                if (response.data.user.avatar) {
                    const cleanedAvatarPath = response.data.user.avatar.replace(/^\/+/, '');
                    setAvatarPreview(`https://ipms.insollex.com/static/${cleanedAvatarPath}`);
                }
            }

            setPassword(''); // clear password input
            setConfirmPassword(''); // clear confirm password input
            setAvatar(null); // clear selected avatar file
        } catch (error) {
            console.error('Error updating profile:', error);
            showSnackbar('Error updating profile', 'error');
        }
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    if (!user) {
        return <p>Loading...</p>;
    }

    return (
        <div style={{ maxWidth: 600, margin: 'auto', padding: '2rem' }}>
            <h1>Edit Profile</h1>
            <form onSubmit={handleSubmit}>
                {/* Profile Picture */}
                <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                    <Avatar
                        src={avatarPreview || getFullAvatarUrl(user?.avatar)}
                        sx={{ width: 100, height: 100 }}
                    />
                    <Button variant="contained" component="label">
                        Upload Profile Picture
                        <input type="file" hidden onChange={handleAvatarChange} accept="image/*" />
                    </Button>
                </Stack>

                {/* First Name */}
                <TextField
                    label="First Name"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                />

                {/* Last Name */}
                <TextField
                    label="Last Name"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                />

                {/* Email Address (read-only) */}
                <TextField
                    label="Email"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={email}
                    InputProps={{
                        readOnly: true,
                    }}
                />

                {/* Bio */}
                <TextField
                    label="Bio"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                />

                {/* New Password */}
                <TextField
                    label="New Password"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    type={showPassword ? 'text' : 'password'} // Use state for visibility
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    error={passwordError} // Show error state
                    helperText={passwordError ? "Passwords must match." : ""} // Show helper text
                    InputProps={{
                        endAdornment: ( // Password visibility toggle
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Confirm Password (NEW FIELD) */}
                <TextField
                    label="Confirm New Password"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    type={showPassword ? 'text' : 'password'} // Use state for visibility
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    error={passwordError} // Show error state
                    helperText={passwordError ? "Passwords must match." : ""} // Show helper text
                    InputProps={{
                        endAdornment: ( // Password visibility toggle
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }}>
                    Save Changes
                </Button>
            </form>

            {/* Snackbar for feedback messages */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default ProfilePage;